import { ReactComponent as TopWidgetArrow } from 'assets/top-widget-arrow.svg';
import classnames from 'classnames';
import { useFormik, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import TagModel from '@mm/state/model/TagModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import { generatePageStructure } from '@mm/state/helpers/filterable-select-helpers';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import { useButtonHandlers } from '../../helpers/widget-helpers/use-button-handlers';
import { makeButton } from '../../../editor/features/build/requestModels/widgets/buttonStackWidget/Button';
import {
  ButtonType,
  GenericButtonModel,
  OptInButtonConfig,
} from '@mm/state/model/ButtonModel';
import FormFieldWrapper from '../../components/FormFieldWrapper/FormFieldWrapper';
import { InputWithEmoji } from '../../components/InputWithEmoji/InputWithEmoji';
import PrimarySelect from '../../components/PrimarySelect/PrimarySelect';
import { ButtonTagsForm } from '../../../editor/features/build/components/widgets/buttons/ButtonConfigform/ButtonTagsForm';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';

type Props = {
  widget: WidgetModel;
  closeButtonArea: () => any;
  onCreate: (button: GenericButtonModel<OptInButtonConfig>) => any;
};

type IceBreakerCreateButtonFormValues = {
  buttonName: string;
  tags: TagModel[];
  pageId: number;
};

const formFields = {
  buttonName: 'buttonName',
  tags: 'tags',
  pageId: 'pageId',
};

const initialValues = {
  buttonName: '',
  pageId: -1,
  tags: [],
};

const validationSchema = Yup.object().shape({
  buttonName: Yup.string().trim().required('Name Required'),
});

const validate = () => async (values: IceBreakerCreateButtonFormValues) => {
  let errors = {};

  try {
    await validationSchema.validate(values, { abortEarly: false });
  } catch (e) {
    errors = yupToFormErrors(e);
  }

  return errors;
};

export const useDialogueDropdownState = (pageId: number | undefined) => {
  const noOption = {
    name: newDialogueName,
    id: -1,
    pageGroupId: -1,
    kind: null as any,
  };
  const pages = [
    ...useSelector((state: StoreState) => state.page.entities),
    noOption,
  ];
  const selectedPage = pages.find(p => p.id === pageId) || noOption;
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const dialogueDropdownOptions = generatePageStructure(
    pages,
    pageGroups,
    true,
  );

  return {
    dialogueDropdownOptions,
    selectedPage,
  };
};

const newDialogueName = 'No';

export const useSubmitForm = (
  widget: WidgetModel,
  closeButtonArea: () => void,
  onCreate: (button: GenericButtonModel<OptInButtonConfig>) => void,
) => {
  const buttonHandlers = useButtonHandlers();
  const { setActiveNode, deselectWidgetButtons } = useContext(PathGraphContext);

  return async (values: IceBreakerCreateButtonFormValues) => {
    const maxSortOrder = Math.max(...widget.buttons.map(b => b.sortOrder), -1);
    const buttonIndex = maxSortOrder + 1;
    const configExtendObject = {
      kind: 'text',
      ...(values.pageId === -1 ? {} : { pageId: values.pageId }),
    };
    const updatedButton = makeButton({
      type: ButtonType.OPT_IN,
      name: values.buttonName,
      sortOrder: buttonIndex,
      tags: values.tags,
      configExtendObject,
    }) as GenericButtonModel<OptInButtonConfig>;

    deselectWidgetButtons?.(widget.buttons);

    const newButton = await buttonHandlers.createButton(widget, updatedButton);

    if (newButton) {
      setActiveNode?.({
        id: newButton?.id,
        type: 'button',
        dialogueId:
          (newButton?.config as OptInButtonConfig).pageId || undefined,
        action: 'open',
      });
      onCreate(newButton as GenericButtonModel<OptInButtonConfig>);
    }

    closeButtonArea();
  };
};

export const OptInCreateButton = (props: Props) => {
  const { widget, closeButtonArea, onCreate } = props;

  const formProps = useFormik<IceBreakerCreateButtonFormValues>({
    initialValues,
    validate: validate(),
    onSubmit: values => submitForm(values),
  });

  const submitForm = useSubmitForm(widget, closeButtonArea, onCreate);

  const { dialogueDropdownOptions, selectedPage } = useDialogueDropdownState(
    Number(formProps.values.pageId),
  );

  const onChangeName = (name: string) => {
    formProps.setFieldValue(formFields.buttonName, name);
  };

  const onTagAdd = (tag: { name: string }) => {
    formProps.setFieldValue(formFields.tags, [...formProps.values.tags, tag]);
  };

  const onTagRemove = (tag: { name: string }) => {
    formProps.setFieldValue(
      formFields.tags,
      formProps.values.tags.filter(e => e.name !== tag.name),
    );
  };

  return (
    <form onSubmit={formProps.handleSubmit} className="opt-in-create-button">
      <FormFieldWrapper
        className="opt-in-create-button__text-wrapper"
        error={formProps.errors.buttonName}
        touched={formProps.touched.buttonName}
      >
        <InputWithEmoji
          name={formFields.buttonName}
          className="opt-in-create-button__text-input-with-emoji-picker"
          textInputClassName="opt-in-create-button__text-input"
          value={formProps.values.buttonName}
          onChange={onChangeName}
          onBlur={() => formProps.setFieldTouched(formFields.buttonName)}
        />
      </FormFieldWrapper>

      <div className="opt-in-create-button__associated-dialogue">
        <div className="opt-in-create-button__associated-dialogue-text">
          Take users to a new Dialogue
        </div>

        <div className="opt-in-create-button__associated-dialogue-input-wrapper">
          <PrimarySelect
            showSearchFilter={true}
            placeholder="Select Dialogue"
            className="opt-in-create-button__associated-dialogue-input"
            onChange={dialogue =>
              formProps.setFieldValue(formFields.pageId, dialogue.id)
            }
            organizedOptions={dialogueDropdownOptions}
            value={selectedPage}
          />
        </div>
      </div>

      <div className="opt-in-create-button__tags-wrapper">
        <ButtonTagsForm
          title="Add a Tag to contacts who click this button"
          kind="add_tags"
          tags={formProps.values.tags}
          onTagAdd={onTagAdd}
          onTagRemove={onTagRemove}
        />
      </div>

      <div className="opt-in-create-button__bottom-area">
        <div className="opt-in-create-button__bottom-buttons">
          <span
            className="opt-in-create-button__cancel-button"
            onClick={closeButtonArea}
          >
            Cancel
          </span>
          <PrimaryButton
            className={classnames('opt-in-create-button__edit-button', {
              'opt-in-create-button__edit-button--disabled': !formProps.isValid,
            })}
            size="lg"
            disabled={!formProps.isValid}
            onClick={formProps.submitForm}
          >
            Create
          </PrimaryButton>
        </div>
      </div>

      <TopWidgetArrow className="opt-in-create-button__bottom-arrow" />
    </form>
  );
};
